import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { Modal } from "@/components/base/Modal";
import { money } from "@/lib/format";
import { StatusBadge } from "@/components/base/StatusBadge";
import { canManageProducts } from "@/lib/permissions";
import { publicationStateMeta, computeChannelPrice } from "@/lib/listing-pricing";
import { effectiveSellingPrice } from "@/lib/products";
import { connectorIsReady } from "@/lib/channel-coverage";
import { connectorDisplayName, type ConnectorIdentityInput } from "@/lib/connector-identity";
import { indexBcListingStorefrontPaths, indexConnectorStorefront, listingLiveLinks } from "@/lib/listing-live-display";
import { ViewLiveLink } from "@/components/feature/ViewLiveLink";
import { ListingWorkspaceModal } from "@/pages/products/detail/components/ListingWorkspaceModal";
import { CatalogueSyncPanel } from "@/pages/products/detail/components/CatalogueSyncPanel";
import PublicationGateNotice from "@/components/feature/PublicationGateNotice";
import { publicationNoticeModel } from "@/lib/listing-publication-gate";
import { firstRelation } from "@/lib/relations";
import {
  type ChannelListing,
  type ReadinessResult,
  fetchChannelListingsForProduct,
  evaluateReadiness,
  copyChannelListing,
  archiveChannelListing,
  requestPublication,
  isInheritingContent,
  canPublish,
  approvalState,
  readinessLabel,
  readinessTone,
  readinessAction,
  readinessConnectorLine,
} from "@/lib/channels";

interface ChannelsTabProps {
  productId: string;
  orgId: string;
  productName: string;
  productDescription: string | null;
  productCategoryName?: string | null;
  productSellingPrice?: number | null;
  variants: { id: string; sku: string | null; option_values: Record<string, string> | null; selling_price: number | null }[];
}

const inputCls =
  "w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500";

export default function ChannelsTab({ productId, orgId, productName, productDescription, productCategoryName, productSellingPrice, variants }: ChannelsTabProps) {
  const { role } = useAuth();
  const queryClient = useQueryClient();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [copySourceId, setCopySourceId] = useState("");
  const [copyTargetConnector, setCopyTargetConnector] = useState("");
  const [readinessOpen, setReadinessOpen] = useState(false);
  const [readinessData, setReadinessData] = useState<ReadinessResult | null>(null);
  const [readinessLoading, setReadinessLoading] = useState(false);
  const [archiveConfirm, setArchiveConfirm] = useState<string | null>(null);
  const [actionError, setActionError] = useState("");
  const [actionBusy, setActionBusy] = useState(false);

  const canManage = canManageProducts(role);

  const { data: listings, isLoading } = useQuery({
    queryKey: ["channel-listings", orgId, productId],
    enabled: !!orgId && !!productId,
    queryFn: () => fetchChannelListingsForProduct(orgId, productId),
  });

  const { data: connectors } = useQuery({
    queryKey: ["connectors-identity", orgId],
    enabled: !!orgId,
    queryFn: async (): Promise<ConnectorIdentityInput[]> => {
      const [cons, bc, ebay] = await Promise.all([
        supabase.from("connectors").select("id, name, provider, status, auth_status").eq("org_id", orgId).order("name"),
        supabase.from("connector_bigcommerce_config").select("connector_id, store_hash, storefront_origin").eq("org_id", orgId),
        supabase.from("connector_ebay_config").select("connector_id, seller_username").eq("org_id", orgId),
      ]);
      const bcBy = Object.fromEntries(
        ((bc.data ?? []) as { connector_id: string; store_hash: string | null; storefront_origin: string | null }[]).map((r) => [r.connector_id, r]),
      );
      const ebayBy = Object.fromEntries(
        ((ebay.data ?? []) as { connector_id: string; seller_username: string | null }[]).map((r) => [r.connector_id, r]),
      );
      return ((cons.data ?? []) as { id: string; name: string; provider: string | null; status: string; auth_status: string }[]).map((c) => ({
        id: c.id,
        name: c.name,
        provider: c.provider,
        authStatus: c.auth_status,
        storeHash: bcBy[c.id]?.store_hash,
        storefrontOrigin: bcBy[c.id]?.storefront_origin,
        sellerUsername: ebayBy[c.id]?.seller_username,
      }));
    },
  });

  const { data: productImages } = useQuery({
    queryKey: ["product-media-workspace", orgId, productId],
    enabled: wizardOpen && !!orgId && !!productId,
    queryFn: async () => {
      const { data } = await supabase
        .from("product_media")
        .select("is_primary, media_assets(id, original_filename)")
        .eq("org_id", orgId)
        .eq("product_id", productId)
        .eq("status", "active");
      return ((data ?? []) as { is_primary: boolean; media_assets: { id: string; original_filename: string | null } | { id: string; original_filename: string | null }[] | null }[]).map((row) => {
        const asset = firstRelation(row.media_assets);
        return {
          mediaAssetId: asset?.id ?? "",
          filename: asset?.original_filename ?? null,
          isPrimary: Boolean(row.is_primary),
        };
      }).filter((row) => row.mediaAssetId);
    },
  });

  const { data: storefront } = useQuery({
    queryKey: ["bc-storefront-origins", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await supabase.from("connector_bigcommerce_config").select("connector_id, storefront_origin, store_hash").eq("org_id", orgId);
      return indexConnectorStorefront((data ?? []) as { connector_id: string; storefront_origin: string | null; store_hash: string | null }[]);
    },
  });

  const { data: bcPaths } = useQuery({
    queryKey: ["bc-storefront-paths", orgId, productId],
    enabled: !!orgId && !!productId,
    queryFn: async () => {
      const { data } = await supabase.from("bigcommerce_products").select("listing_id, storefront_custom_url").eq("org_id", orgId).eq("product_id", productId);
      return indexBcListingStorefrontPaths((data ?? []) as { listing_id: string; storefront_custom_url: string | null }[]);
    },
  });

  const handleCopy = async () => {
    if (!copySourceId || !copyTargetConnector) return;
    setActionError("");
    setActionBusy(true);
    try {
      const result = await copyChannelListing(orgId, copySourceId, copyTargetConnector);
      if (!result.success) {
        setActionError(result.error || "Failed to copy listing.");
      } else {
        setCopyOpen(false);
        queryClient.invalidateQueries({ queryKey: ["channel-listings", orgId, productId] });
      }
    } catch (err) {
      setActionError((err as Error).message);
    }
    setActionBusy(false);
  };

  const handleArchive = async (listingId: string) => {
    setActionError("");
    setActionBusy(true);
    try {
      await archiveChannelListing(orgId, listingId);
      setArchiveConfirm(null);
      queryClient.invalidateQueries({ queryKey: ["channel-listings", orgId, productId] });
    } catch (err) {
      setActionError((err as Error).message);
    }
    setActionBusy(false);
  };

  const checkReadiness = async (listingId: string) => {
    setReadinessLoading(true);
    setReadinessOpen(true);
    try {
      const result = await evaluateReadiness(orgId, listingId);
      setReadinessData(result);
    } catch {
      setReadinessData({ status: "BLOCKED", blocking: 1, warning: 0, checks: [{ type: "blocking", code: "evaluation_failed", message: "Could not evaluate readiness." }] });
    }
    setReadinessLoading(false);
  };

  const requestPublish = async (listingId: string) => {
    setActionError("");
    try {
      const result = await requestPublication(orgId, listingId);
      if (!result.success) {
        setActionError(result.error || "Publication request failed");
      }
      queryClient.invalidateQueries({ queryKey: ["channel-listings", orgId, productId] });
    } catch (err) {
      setActionError((err as Error).message);
    }
  };

  const availableConnectors = connectors?.filter((c) =>
    !listings?.some((l) => l.connector_id === c.id)
  ) ?? [];

  const identityById = new Map((connectors ?? []).map((c) => [c.id, c]));
  const connectorAuthStatus = new Map((connectors ?? []).map((c) => [c.id, c.authStatus ?? ""]));

  const listingChannelLabel = (listing: ChannelListing) => {
    const ident = identityById.get(listing.connector_id);
    if (ident) return connectorDisplayName(ident);
    return listing.connector?.name || "Channel";
  };

  return (
    <div className="space-y-6">
      {actionError && (
        <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{actionError}</div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-800">Channel Listings</h3>
          <p className="text-xs text-slate-400">Manage how this product is sold on external channels</p>
        </div>
        {canManage && (
          <div className="flex items-center gap-2">
            {listings && listings.length > 0 && availableConnectors.length > 0 && (
              <button
                onClick={() => { setCopySourceId(""); setCopyTargetConnector(""); setActionError(""); setCopyOpen(true); }}
                className="px-3 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-medium rounded-lg whitespace-nowrap flex items-center gap-1"
              >
                <i className="ri-file-copy-line" /> Copy listing
              </button>
            )}
            {availableConnectors.length > 0 && (
              <button
                onClick={() => { setActionError(""); setWizardOpen(true); }}
                className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded-lg whitespace-nowrap flex items-center gap-1"
              >
                <i className="ri-add-line" /> Create listing
              </button>
            )}
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <i className="ri-loader-4-line animate-spin text-slate-400 text-lg" />
        </div>
      ) : !listings || listings.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <i className="ri-store-2-line text-3xl text-slate-300 mb-3 block" />
          <p className="text-sm text-slate-600 font-medium">Not listed on any channels yet</p>
          <p className="text-xs text-slate-400 mt-1 max-w-sm mx-auto">
            Create a channel listing to publish this product to eBay, BigCommerce, Amazon, or other marketplaces.
          </p>
          {canManage && availableConnectors.length > 0 && (
            <button
              onClick={() => { setActionError(""); setWizardOpen(true); }}
              className="mt-4 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg whitespace-nowrap"
            >
              Create first listing
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <CatalogueSyncPanel
            orgId={orgId}
            productId={productId}
            listings={listings.map((listing) => ({
              id: listing.id,
              connectorName: connectorDisplayName({
                id: listing.connector_id,
                name: listing.connector?.name ?? "",
                provider: listing.connector?.provider ?? null,
                authStatus: listing.connector?.auth_status,
              }),
              provider: listing.connector?.provider ?? null,
              dirty: listing.dirty_domains,
              publicationStatus: listing.publication_status,
            }))}
          />
          {listings.map((listing) => {
            const gateModel = publicationNoticeModel({
              status: listing.status,
              publicationStatus: listing.publication_status,
              connectionReady: connectorIsReady(connectorAuthStatus.get(listing.connector_id)),
              approval: approvalState(listing),
              readinessStatus: listing.readiness_status ?? null,
              approvedVersion: listing.approved_version,
            });
            const gate = gateModel.gate;
            return (
            <div key={listing.id} className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link to={`/listings/${listing.id}`} className="text-sm font-semibold text-slate-800 hover:text-emerald-700">
                      {listing.channel_title || productName}
                    </Link>
                    {isInheritingContent(listing, "title") && (
                      <span className="text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">inherited title</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className="text-xs text-slate-500">
                      {listingChannelLabel(listing)}
                      {listing.connector?.provider ? ` · ${listing.connector.provider}` : ""}
                    </span>
                    <StatusBadge status={listing.status} />
                    {(() => {
                      const confirmed = listing.provider_confirmed_id || listing.external_listing_id;
                      const meta = publicationStateMeta({
                        publicationStatus: listing.publication_status,
                        status: listing.status,
                        hasDirty: (listing.dirty_domains?.length ?? 0) > 0,
                        connectionReady: connectorIsReady(connectorAuthStatus.get(listing.connector_id)),
                        lastError: listing.last_error,
                        providerConfirmedListed: Boolean(confirmed),
                      });
                      const live = listingLiveLinks({
                        provider: listing.connector?.provider,
                        externalId: confirmed,
                        storefrontOrigin: storefront?.originByConnector[listing.connector_id],
                        storeHash: storefront?.storeHashByConnector[listing.connector_id],
                        customUrl: bcPaths?.[listing.id],
                      }).liveUrl;
                      return (
                        <>
                          <span className="text-xs font-medium text-slate-600">{meta.label}</span>
                          <ViewLiveLink href={live} />
                        </>
                      );
                    })()}
                    {listing.readiness_status && listing.readiness_status !== "unknown" && (
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                        readinessTone(listing.readiness_status) === "ready"
                          ? "bg-emerald-50 text-emerald-700"
                          : readinessTone(listing.readiness_status) === "warning"
                          ? "bg-amber-50 text-amber-700"
                          : "bg-red-50 text-red-700"
                      }`}>
                        {readinessLabel(listing.readiness_status)}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    {listing.variant_count ?? 0} variant{(listing.variant_count ?? 0) === 1 ? "" : "s"} ·
                    {listing.price_strategy === "inherit" ? " inherits price" : " custom pricing"}
                    {` · ${money(computeChannelPrice(listing.price_strategy, Number(effectiveSellingPrice(variants[0]?.selling_price, productSellingPrice) ?? 0), {
                      percentageMarkup: (listing.provider_config as { pricing?: { percentage_markup?: number } } | undefined)?.pricing?.percentage_markup,
                      fixedMarkup: (listing.provider_config as { pricing?: { fixed_markup?: number } } | undefined)?.pricing?.fixed_markup,
                      fixedOverride: (listing.provider_config as { pricing?: { fixed_override?: number } } | undefined)?.pricing?.fixed_override,
                    }))}`}
                    {" · "}
                    {listing.inventory_strategy === "all_available" ? "all inventory" : listing.inventory_strategy}
                  </p>
                  {listing.channel_category_name && (
                    <p className="text-xs text-slate-500 mt-1">Category: {listing.channel_category_name}</p>
                  )}
                  {listing.media_strategy === "listing_override" && listing.primary_media_filename && (
                    <p className="text-xs text-slate-500 mt-1">Primary image: {listing.primary_media_filename}</p>
                  )}
                  {listing.last_error && (
                    <p className="text-xs text-red-600 mt-1">{listing.last_error}</p>
                  )}
                  {canPublish(listing.status) && !gate.allowed && (
                    <div className="mt-2 max-w-md">
                      <PublicationGateNotice model={gateModel} variant="compact" />
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => checkReadiness(listing.id)}
                    className="px-2 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-600 text-xs font-medium rounded-lg whitespace-nowrap"
                  >
                    Readiness
                  </button>
                  {canPublish(listing.status) && canManage && (gate.allowed ? (
                    <button
                      onClick={() => requestPublish(listing.id)}
                      className="px-2 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded-lg whitespace-nowrap"
                    >
                      Publish
                    </button>
                  ) : (
                    <span
                      data-testid={`channel-publish-blocked-${listing.id}`}
                      className={`px-2 py-1.5 border text-xs font-medium rounded-lg whitespace-nowrap max-w-[220px] truncate ${gate.tone === "bad" ? "bg-red-50 border-red-100 text-red-700" : "bg-slate-100 border-slate-200 text-slate-500"}`}
                      title={gate.message}
                    >
                      {gate.label}
                    </span>
                  ))}
                  {canManage && (
                    <button
                      onClick={() => setArchiveConfirm(listing.id)}
                      className="px-2 py-1.5 bg-white border border-slate-200 hover:bg-red-50 text-slate-600 hover:text-red-600 text-xs font-medium rounded-lg whitespace-nowrap"
                    >
                      Archive
                    </button>
                  )}
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}

      <ListingWorkspaceModal
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        orgId={orgId}
        productId={productId}
        productName={productName}
        productDescription={productDescription}
        productCategoryName={productCategoryName}
        productSellingPrice={productSellingPrice}
        connectors={availableConnectors}
        variants={variants}
        images={productImages ?? []}
        onCreated={() => queryClient.invalidateQueries({ queryKey: ["channel-listings", orgId, productId] })}
      />

      {/* Copy Listing */}
      <Modal
        open={copyOpen}
        onClose={() => setCopyOpen(false)}
        title="Copy Listing to Another Channel"
        footer={
          <>
            <button onClick={() => setCopyOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">Cancel</button>
            <button onClick={handleCopy} disabled={actionBusy || !copySourceId || !copyTargetConnector} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap">
              {actionBusy ? "Copying..." : "Copy Listing"}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          {actionError && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{actionError}</div>}
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Source listing</label>
            <select value={copySourceId} onChange={(e) => setCopySourceId(e.target.value)} className={inputCls}>
              <option value="">Select...</option>
              {listings?.map((l) => (
                <option key={l.id} value={l.id}>{l.channel_title || productName} · {listingChannelLabel(l)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Target channel</label>
            <select value={copyTargetConnector} onChange={(e) => setCopyTargetConnector(e.target.value)} className={inputCls}>
              <option value="">Select...</option>
              {availableConnectors.map((c) => (
                <option key={c.id} value={c.id}>{connectorDisplayName(c)}</option>
              ))}
            </select>
          </div>
          <p className="text-xs text-slate-400">
            Copies configuration, variant selection, media assignments, and attribute values. External IDs and publication state are NOT copied.
          </p>
        </div>
      </Modal>

      {/* Readiness Modal */}
      <Modal
        open={readinessOpen}
        onClose={() => setReadinessOpen(false)}
        title="Listing Readiness"
        footer={
          <button onClick={() => setReadinessOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">
            Close
          </button>
        }
      >
        {readinessLoading ? (
          <div className="py-8 text-center">
            <i className="ri-loader-4-line animate-spin text-slate-400 text-lg" />
            <p className="text-sm text-slate-500 mt-2">Evaluating...</p>
          </div>
        ) : readinessData ? (
          <div className="space-y-3">
            <div className={`text-sm font-semibold ${
                readinessData.status === "READY"
                  ? "text-emerald-700"
                : readinessData.status === "READY_WITH_WARNINGS"
                ? "text-amber-700"
                : "text-red-700"
            }`}>
              Status: {readinessData.status.toUpperCase()}
              {readinessData.blocking > 0 && ` · ${readinessData.blocking} blocking`}
              {readinessData.warning > 0 && ` · ${readinessData.warning} warning${readinessData.warning === 1 ? "" : "s"}`}
            </div>
            {readinessConnectorLine(readinessData) && (
              <p className="text-sm text-slate-700">{readinessConnectorLine(readinessData)}</p>
            )}
            {readinessData.checks.length === 0 ? (
              <div className="text-sm text-slate-600">
                <p>All content and marketplace data checks passed.</p>
                <p className="text-xs text-slate-500 mt-1">
                  This does not authorize publication. Approval, an authorized channel account and an unresolved remote outcome are checked separately.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {readinessData.checks.map((check, i) => (
                  <div key={i} className={`flex items-start gap-2 p-2 rounded-lg ${
                    check.type === "blocking" ? "bg-red-50" : "bg-amber-50"
                  }`}>
                    <i className={`${check.type === "blocking" ? "ri-close-circle-line text-red-500" : "ri-alert-line text-amber-500"} mt-0.5`} />
                    <div>
                      <p className={`text-sm font-medium ${check.type === "blocking" ? "text-red-700" : "text-amber-700"}`}>
                        {check.message}
                      </p>
                      {readinessAction(check.code) && (
                        <p className="text-xs text-slate-600 font-medium mt-0.5">
                          <i className="ri-arrow-right-line mr-1" />{readinessAction(check.code)}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : null}
      </Modal>

      {/* Archive Confirm */}
      <Modal
        open={!!archiveConfirm}
        onClose={() => setArchiveConfirm(null)}
        title="Archive Listing?"
        footer={
          <>
            <button onClick={() => setArchiveConfirm(null)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">Cancel</button>
            <button onClick={() => archiveConfirm && handleArchive(archiveConfirm)} disabled={actionBusy} className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap">
              {actionBusy ? "Archiving..." : "Archive"}
            </button>
          </>
        }
      >
        <p className="text-sm text-slate-600">
          This will archive the listing. If it is currently published, it will be marked as ended and may require manual action on the marketplace.
        </p>
      </Modal>
    </div>
  );
}
